import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import type {
  CreativeDecisionProjection,
  CreativeDecisionRecommendation,
  CreativeDecisionCopilotAnswer,
  CreativeDecisionCopilotAnswerRequest,
  CreativeLifecycleEvent,
  CreativeStageMetric,
  PromiseOutcomeSignalDiagnostic,
  CreativeReportEnrichmentItem,
  ProviderSourceReportEnrichmentItem,
} from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { CreativeContributionWaterfall } from './CreativeContributionWaterfall';
import { CreativeActualVsPredictedCurve } from './CreativeActualVsPredictedCurve';
import { CreativeCalendarDynamics } from './CreativeCalendarDynamics';
import { CreativeProductEconomicsSection } from './CreativeProductEconomicsSection';
import { CreativeFatigueCurveSection } from './CreativeFatigueCurveSection';
import { CreativeWatchOnlyPltvSection } from './CreativeWatchOnlyPltvSection';
import { CreativePreviewPanel } from './CreativeProviderSourceEnrichment';
import type { FunnelInsightsApiClient } from '../../api/insightsClient';
import { PltvWithCac } from '../PltvWithCac';
import {
  formatMoneyBinding,
  formatPercent,
  formatRoas,
  formatSampleCount,
  humanizeCreativeReasonCode,
  valueBasisLabel,
} from './creativeDecisionFormat';

interface CreativeDecisionDrawerProps {
  decision: CreativeDecisionProjection | null;
  onClose: () => void;
  onPrimaryAction?: (decision: CreativeDecisionProjection) => void;
  detailLoading?: boolean;
  detailError?: string | null;
  enrichmentItem?: CreativeReportEnrichmentItem;
  enrichmentLoading?: boolean;
  providerSourceItem?: ProviderSourceReportEnrichmentItem;
  providerSourceLoading?: boolean;
  projectId?: string;
  providerSourceAssociationWindow?: { from: string; to: string };
  onProviderSourceSynced?: () => void;
  sourceSyncClient?: Pick<
    FunnelInsightsApiClient,
    'preflightProviderCreativeSourceReportRow' | 'syncMetaCreativeSources' | 'getProviderCreativeSourceSync'
  >;
  onRetryDetail?: () => void;
  onAskEvidence?: (request: CreativeDecisionCopilotAnswerRequest) => Promise<CreativeDecisionCopilotAnswer>;
}

/** S7: campaign-grain CAC from the product-economics contract (major units). */
function decisionCacBeside(decision: Pick<CreativeDecisionProjection, 'productEconomics'>): { value: number | null | undefined; reason?: string } {
  const line = decision.productEconomics?.products.find(product => product.projected?.checksToCoverCac?.cacMinor !== undefined);
  const cac = line?.projected?.checksToCoverCac;
  if (!cac || cac.cacMinor === undefined) return { value: null, reason: 'campaign-grain CAC not served for this creative' };
  return { value: cac.cacMinor / (10 ** (cac.currencyExponent ?? 2)) };
}

export function CreativeDecisionDrawer({
  decision,
  onClose,
  onPrimaryAction,
  detailLoading = false,
  detailError,
  enrichmentItem,
  enrichmentLoading = false,
  providerSourceItem,
  providerSourceLoading = false,
  projectId,
  providerSourceAssociationWindow,
  onProviderSourceSynced,
  sourceSyncClient,
  onRetryDetail,
  onAskEvidence,
}: CreativeDecisionDrawerProps): React.ReactElement {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Drawer
      anchor="right"
      open={Boolean(decision)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: fullScreen ? '100%' : 560,
          maxWidth: '100vw',
        },
        'data-testid': CREATIVE_DECISIONS_TEST_IDS.drawer,
      }}
    >
      {decision && (
        <Stack sx={{ minHeight: '100%' }}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ p: 2.5, pb: 2 }}>
            <IconButton
              aria-label="Close creative decision"
              onClick={onClose}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerClose}
              sx={{ mt: -0.5 }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="overline" color="text.secondary">
                {decision.display?.decisionUnitLabel ?? decision.deployment.decisionUnitKind.replaceAll('_', ' ')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 750 }} noWrap>
                {decision.display?.deploymentName ?? decision.deployment.providerResourceName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${providerLabel(decision.deployment.provider)} · ${decision.deployment.providerAccountId}`}
              </Typography>
              {decision.detailSource && (
                // W-R5 seam (part c): the drawer's data-age carrier, mirroring
                // the portfolio SnapshotBanner wording. Absent field (older
                // payloads) renders nothing — never a fabricated age.
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerDataAge}
                >
                  {decision.detailSource.servedFrom === 'snapshot'
                    ? `base row computed ${decision.detailSource.snapshotComputedAt
                      ? new Date(decision.detailSource.snapshotComputedAt).toLocaleString()
                      : '—'} · snapshot · evidence sections live`
                    : 'computed just now · live query'}
                </Typography>
              )}
            </Box>
          </Stack>
          <Divider />

          <Stack spacing={2.5} sx={{ p: 2.5, pb: 10 }}>
            {detailLoading && (
              <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.detailLoading}>
                Loading full decision evidence…
              </Alert>
            )}
            {detailError && (
              <Alert
                severity="error"
                data-testid={CREATIVE_DECISIONS_TEST_IDS.detailError}
                action={onRetryDetail ? (
                  <Button
                    color="inherit"
                    onClick={onRetryDetail}
                    data-testid={CREATIVE_DECISIONS_TEST_IDS.detailRetry}
                  >
                    Retry detail
                  </Button>
                ) : undefined}
              >
                {`Full decision evidence is unavailable: ${detailError}. The portfolio summary remains visible.`}
              </Alert>
            )}
            <CreativePreviewPanel
              exactItem={enrichmentItem}
              providerSourceItem={providerSourceItem}
              exactLoading={enrichmentLoading}
              providerSourceLoading={providerSourceLoading}
              projectId={projectId}
              associationWindow={providerSourceAssociationWindow}
              onProviderSourceSynced={onProviderSourceSynced}
              sourceSyncClient={sourceSyncClient}
            />
            <IdentitySection decision={decision} />
            <ArtifactTaxonomySection decision={decision} />
            <MoneySection decision={decision} />
            <CreativeCalendarDynamics dynamics={decision.calendarDynamics} />
            <CreativeProductEconomicsSection economics={decision.productEconomics} />
            <CreativeFatigueCurveSection
              curve={decision.fatigueCurve}
              currency={decision.money.grossCollected?.reportingCurrency
                ?? decision.money.grossCollected?.originalCurrency}
              currencyExponent={decision.money.grossCollected?.currencyExponent}
            />
            {/* S7 / D-J: the watch-only band dynamics never render without the
                campaign-grain CAC beside them (the product-economics contract:
                CAC is campaign-grain and does not decompose per product). */}
            <PltvWithCac
              cac={decisionCacBeside(decision)}
              testId={CREATIVE_DECISIONS_TEST_IDS.drawerWatchOnlyCacLine}
              pltv={<CreativeWatchOnlyPltvSection watchOnly={decision.watchOnlyPltv} />}
            />
            <RecommendationSection decision={decision} />
            <LifecycleSection events={decision.lifecycleEvents ?? []} currentStage={decision.lifecycleStage} />
            <DecisionHistorySection decision={decision} />
            <ContextDiagnosticsSection decision={decision} />
            <PromiseLineageSection decision={decision} />
            <DecisionTrustSection decision={decision} />
            <MonetizationSection decision={decision} />
            <SignalSection metrics={decision.stageMetrics ?? []} />
            <EvidenceCopilotSection decision={decision} onAskEvidence={onAskEvidence} />
            <EvidenceSection decision={decision} />
          </Stack>

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              mt: 'auto',
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={<ScienceOutlinedIcon />}
              onClick={() => onPrimaryAction?.(decision)}
              disabled={!onPrimaryAction || decision.recommendation.primaryAction === 'none'}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerPrimaryAction}
            >
              {primaryActionLabel(decision.recommendation)}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Review only. Segmently will not change provider ads or spend from this surface.
            </Typography>
          </Box>
        </Stack>
      )}
    </Drawer>
  );
}

function IdentitySection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  return (
    <Section title="Deployment & artifact" testId={CREATIVE_DECISIONS_TEST_IDS.drawerIdentity}>
      <DefinitionGrid rows={[
        ['Provider identity', decision.deployment.providerResourceName],
        ['Origin', decision.deployment.origin.replaceAll('_', ' ')],
        ['Campaign', decision.display?.campaignName ?? decision.display?.campaignId ?? 'n/a'],
        ['Provider created', formatDateTime(decision.deployment.providerCreatedAt)],
        ['First observed', formatDateTime(decision.deployment.firstObservedAt)],
        ['First-party join grain', decision.capability.firstPartyJoinGrain.replaceAll('_', ' ')],
        ['Outcome grain', decision.capability.providerOutcomeGrain.replaceAll('_', ' ')],
      ]} />
      {decision.display?.badges.length ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          {decision.display.badges.map((badge) => (
            <Chip key={badge} size="small" variant="outlined" label={badge.replaceAll('_', ' ')} />
          ))}
        </Stack>
      ) : null}
    </Section>
  );
}

function ArtifactTaxonomySection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const taxonomySnapshots = decision.artifactSnapshots.flatMap((artifact) => (
    artifact.taxonomy ? [{ artifact, taxonomy: artifact.taxonomy }] : []
  ));
  return (
    <Section title="Creative learning taxonomy" testId={CREATIVE_DECISIONS_TEST_IDS.drawerTaxonomy}>
      {taxonomySnapshots.length === 0 ? (
        <Alert severity="info">
          Structured concept, hook, angle, claim, format, creator, placement, funnel, motive, and offer taxonomy is unavailable for this artifact. This is normal for a newly connected external account; Segmently has not inferred labels from mutable display copy.
        </Alert>
      ) : (
        <Stack spacing={1.25}>
          {taxonomySnapshots.map(({ artifact, taxonomy }) => (
            <Box key={`${artifact.artifactSnapshotKey}:${taxonomy.taxonomySnapshotId}`} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                {`${taxonomy.source.replaceAll('_', ' ')} · ${taxonomy.taxonomyVersion} · ${formatDateTime(taxonomy.observedAt)}`}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {taxonomy.values.map((value) => (
                  <Chip
                    key={`${value.dimension}:${value.valueKey}:${value.evidenceRef}`}
                    size="small"
                    variant="outlined"
                    label={`${value.dimension.replaceAll('_', ' ')} · ${value.valueKey.replaceAll('_', ' ')}`}
                  />
                ))}
              </Stack>
              {taxonomy.values.length === 0 && (
                <Typography variant="body2" color="text.secondary">No reviewed taxonomy values in this immutable snapshot.</Typography>
              )}
              {taxonomy.learningFindings?.length ? (
                <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Qualified learning ledger
                  </Typography>
                  {taxonomy.learningFindings.map((finding) => (
                    <Box
                      key={finding.learningSnapshotId}
                      sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        <Chip size="small" label={`${finding.dimension.replaceAll('_', ' ')} · ${finding.valueKey.replaceAll('_', ' ')}`} />
                        <Chip size="small" variant="outlined" label={finding.findingDirection} />
                        <Chip size="small" variant="outlined" label={finding.evidenceClass} />
                        <Chip size="small" variant="outlined" label={`replication ${finding.replicationState.replaceAll('_', ' ')}`} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {`Effect ${formatEffect(finding.effectEstimate)} · valid until ${formatDateTime(finding.validUntil)}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                        {`Selection ${finding.selectionRuleRef} · post-selection ${finding.postSelectionEvidenceRef}`}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Alert severity="info" sx={{ mt: 1.25 }}>
                  No qualified post-selection learning is available for this exact taxonomy snapshot. Taxonomy labels alone are not findings.
                </Alert>
              )}
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            Taxonomy helps group bounded learnings across contexts. It does not pool deployment economics or create a global winner for a creative. Qualified findings remain scoped to their exact dimension, experiment, selection rule, validity window, and replication state.
          </Typography>
        </Stack>
      )}
    </Section>
  );
}

function MoneySection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const { money } = decision;
  return (
    <Section title="Money impact" testId={CREATIVE_DECISIONS_TEST_IDS.drawerMoney}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        <MoneyTile label="Spend in scope" value={formatMoneyBinding(money.selectedWindowSpend, true)} />
        <MoneyTile label="Current spend / day" value={formatMoneyBinding(money.currentDailySpend, true)} />
        <MoneyTile label="Share of active spend" value={formatPercent(money.activeSpendShare)} />
        <MoneyTile label="Next 7 days at pace" value={formatMoneyBinding(money.unchangedPaceSevenDayExposure, true)} />
        <MoneyTile label="Gross collected" value={formatMoneyBinding(money.grossCollected, true)} />
        <MoneyTile label="Net collected" value={formatMoneyBinding(money.netCollected, true)} />
        <MoneyTile label="Settlement cash" value={formatMoneyBinding(money.settlementCash, true)} />
        <MoneyTile label="Contribution before ads" value={formatMoneyBinding(money.realizedContributionBeforeAdSpend, true)} />
        <MoneyTile label="Contribution after ads" value={formatMoneyBinding(money.realizedContributionAfterAdSpend, true)} />
        <MoneyTile label="Projected contribution P10" value={formatMoneyBinding(money.projectedContributionP10, true)} />
        <MoneyTile label="Projected contribution P50" value={formatMoneyBinding(money.projectedContributionP50, true)} />
        <MoneyTile label="Projected contribution P90" value={formatMoneyBinding(money.projectedContributionP90, true)} />
        <MoneyTile label="Contribution pROAS P10" value={formatRoas(money.projectedContributionRoasP10)} />
        <MoneyTile label="Contribution pROAS P50" value={formatRoas(money.projectedContributionRoasP50)} />
        <MoneyTile label="Contribution pROAS P90" value={formatRoas(money.projectedContributionRoasP90)} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {`Gross: ${valueBasisLabel(money.grossBasis)} · net: ${valueBasisLabel(money.netCollectedBasis ?? money.realizedValueBasis)} · settlement: ${valueBasisLabel(money.settlementBasis)} · contribution: ${valueBasisLabel(money.contributionBasis)} · projected contribution: ${valueBasisLabel(money.projectedValueBasis)} · as of ${formatDateTime(decision.freshness.asOf)}`}
      </Typography>
      {money.partialControlsBasis && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {`Gross rides the partial-controls basis: gross collections are control-vouched through the settled boundary, while these control categories stay excluded from the acceptance window: ${(money.unreconciledControlCategories ?? []).join(', ') || 'unknown'}. Gross numbers are always labeled gross — never net.`}
        </Alert>
      )}
      {(money.grossExcludedCellCount ?? 0) > 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {`Gross is a labeled subset: ${money.grossExcludedCellCount} product×cohort cell(s) are excluded from the total (live-edge collections beyond the settled boundary or per-line basis blockers). They rejoin automatically as settlement evidence matures.`}
        </Alert>
      )}
      {money.currentPartialDaySpend && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {`Current partial day is separate: ${formatMoneyBinding(money.currentPartialDaySpend, true)}. It is not included in stable pace.`}
        </Alert>
      )}
      <CreativeContributionWaterfall decision={decision} />
    </Section>
  );
}

function RecommendationSection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const recommendation = decision.recommendation;
  return (
    <Section title="Current recommendation" testId={CREATIVE_DECISIONS_TEST_IDS.drawerRecommendation}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <DecisionChip decision={recommendation.decision} />
        <Chip size="small" variant="outlined" label={decision.dataState.replaceAll('_', ' ')} />
        {recommendation.approvalRequired && <Chip size="small" color="warning" label="Human approval required" />}
      </Stack>
      <Typography variant="body2" sx={{ mt: 1.25 }}>
        {recommendation.reasonCodes.length
          ? recommendation.reasonCodes.map((reason) => humanizeCreativeReasonCode(reason)).join(' · ')
          : 'No decision reason is available.'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {recommendation.validUntil ? `Valid until ${formatDateTime(recommendation.validUntil)}` : 'No decision expiry is available.'}
      </Typography>
      {(recommendation.nextReadAt ?? decision.maturity?.nextReadAt) && (
        <Alert severity="info" sx={{ mt: 1.25 }}>
          {`Next registered read: ${formatDateTime(recommendation.nextReadAt ?? decision.maturity?.nextReadAt)}.`}
        </Alert>
      )}
    </Section>
  );
}

function LifecycleSection({
  events,
  currentStage,
}: {
  events: CreativeLifecycleEvent[];
  currentStage: CreativeDecisionProjection['lifecycleStage'];
}): React.ReactElement {
  return (
    <Section title="Lifecycle evidence" testId={CREATIVE_DECISIONS_TEST_IDS.drawerLifecycle}>
      {events.length === 0 ? (
        <Alert severity="info">
          {`Current observed stage: ${currentStage}. Earlier provider-only history is unavailable and has not been inferred.`}
        </Alert>
      ) : (
        <Stack spacing={1.25}>
          {events.map((event, index) => (
            <Stack key={event.eventId} direction="row" spacing={1.25} alignItems="flex-start">
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: index === events.length - 1 ? 'primary.main' : 'divider', mt: 0.7 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 650 }}>
                  {`${event.eventKind.replaceAll('_', ' ')} · ${event.stage.replaceAll('_', ' ')}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {`${formatDateTime(event.occurredAt)} · ${event.source.replaceAll('_', ' ')}`}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Section>
  );
}

function DecisionHistorySection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const history = decision.decisionHistory ?? [];
  return (
    <Section title="Decision revision history" testId={`${CREATIVE_DECISIONS_TEST_IDS.drawerRecommendation}-history`}>
      {history.length === 0 ? (
        <Alert severity="info">No prior recommendation revision is available for this projection.</Alert>
      ) : (
        <Stack spacing={1}>
          {history.map((revision, index) => (
            <Box key={revision.decisionRevisionId} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {`${revision.decision.replaceAll('_', ' ')} · ${revision.dataState.replaceAll('_', ' ')}`}
                </Typography>
                <Chip size="small" variant="outlined" label={index === history.length - 1 ? 'current' : 'superseded'} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {`${formatDateTime(revision.effectiveAt)} · ${revision.changeReason.replaceAll('_', ' ')} · ${revision.decisionRevisionId}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {revision.reasonCodes.map((reason) => reason.replaceAll('_', ' ')).join(' · ') || 'No reason code'}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Section>
  );
}

function ContextDiagnosticsSection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const artifact = decision.artifactContext;
  const audience = artifact?.audienceDistributionDiagnostics;
  const promise = decision.decisionSupport?.promiseContinuity;
  const contexts = artifact?.deploymentContexts ?? [];
  const aggregate = artifact?.aggregateMoney;
  const dispersion = artifact?.dispersion;
  return (
    <Section title="Context heterogeneity & promise" testId={`${CREATIVE_DECISIONS_TEST_IDS.drawerEvidence}-context`}>
      <DefinitionGrid rows={[
        ['Artifact reuse', artifact ? `${artifact.reuseCount} deployment contexts` : 'n/a'],
        ['Cross-context result', artifact?.heterogeneityState?.replaceAll('_', ' ') ?? 'unavailable'],
        ['Comparison basis', artifact?.comparisonBasis?.replaceAll('_', ' ') ?? 'unavailable'],
        ['Selected spend across contexts', aggregate?.state === 'available'
          ? formatMoneyBinding(aggregate.selectedWindowSpend)
          : 'unavailable · currencies, FX, or cost basis are incompatible'],
        ['Current artifact flow', aggregate?.currentDailySpend
          ? `${formatMoneyBinding(aggregate.currentDailySpend)} / day`
          : 'unavailable'],
        ['Observed context dispersion', formatArtifactDispersion(dispersion)],
        ['Causal portability', artifact?.causalPortability?.replaceAll('_', ' ') ?? 'not established'],
        ['Audience distribution', audience?.state ?? 'unavailable'],
        ['Optimization mode', audience?.optimizationMode ?? 'n/a'],
        ['Max standardized difference', formatNumber(audience?.standardizedDifferenceMax)],
        ['Distribution overlap', formatPercentValue(audience?.overlapScore)],
        ['Promise continuity', promise?.status === 'available' ? 'diagnostic only' : 'unavailable'],
        ['Promise diagnostic score', formatPercentValue(promise?.diagnosticScore)],
      ]} />
      {audience?.state === 'divergent' && (
        <Alert severity="warning" sx={{ mt: 1.25 }}>
          Delivered audiences differ materially. Treat this as a delivered-policy comparison, not an intrinsic creative effect.
        </Alert>
      )}
      {contexts.length > 1 && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <Alert severity="info">
            Deployment spend and value remain separate. The range below is diagnostic and never becomes a pooled pROAS or a global creative winner.
          </Alert>
          {contexts.map((context) => (
            <Box
              key={context.deploymentKey}
              sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {context.campaignName ?? context.campaignId ?? context.campaignFamily.replaceAll('_', ' ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {`${providerLabel(context.provider)} · ${context.providerResourceName}`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                  <Chip size="small" label={context.strongestEvidenceClass} />
                  <Chip size="small" variant="outlined" label={context.decision.replaceAll('_', ' ')} />
                </Stack>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 0.75,
                  mt: 1,
                }}
              >
                <ContextFact label="Selected spend" value={formatMoneyBinding(context.selectedWindowSpend)} />
                <ContextFact
                  label="Current flow"
                  value={context.currentDailySpend
                    ? `${formatMoneyBinding(context.currentDailySpend)} / day · ${formatPercent(context.artifactCurrentSpendShare)} of artifact flow`
                    : 'unavailable'}
                />
                <ContextFact
                  label="Value returned"
                  value={context.realizedContributionBeforeAdSpend
                    ? `${formatMoneyBinding(context.realizedContributionBeforeAdSpend)} contribution · ROAS ${formatRoas(context.realizedContributionRoas)}`
                    : context.netCollected
                      ? `${formatMoneyBinding(context.netCollected)} net collected`
                      : 'first-party value unavailable'}
                />
                <ContextFact
                  label="Projected contribution"
                  value={context.projectedContributionP50
                    ? `${formatMoneyBinding(context.projectedContributionP50)} · pROAS P50 ${formatRoas(context.projectedContributionRoasP50)}`
                    : 'unavailable'}
                />
                <ContextFact
                  label="Monetization"
                  value={context.monetizationContexts.map(artifactMonetizationLabel).join(' · ') || 'unavailable'}
                />
                <ContextFact
                  label="Context changes"
                  value={context.differingDimensions.length
                    ? context.differingDimensions.map((value) => value.replaceAll('_', ' ')).join(' · ')
                    : 'no represented dimension differs'}
                />
              </Box>
              <ContextComparisonStatistics decision={decision} context={context} />
            </Box>
          ))}
          {artifact?.scopedLearning && (
            <Alert severity={artifact.heterogeneityState === 'divergent' ? 'warning' : 'info'}>
              {artifact.scopedLearning}
            </Alert>
          )}
        </Stack>
      )}
      {promise?.status === 'available' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {`Matched ${promise.matchedRefs?.length ?? 0} · missing ${promise.missingRefs?.length ?? 0} · conflicting ${promise.conflictingRefs?.length ?? 0}. Similarity cannot raise evidence class or trigger scale/stop.`}
        </Typography>
      )}
    </Section>
  );
}

const PROMISE_STAGE_ORDER = [
  'ad_promise',
  'landing_first_fold',
  'quiz_result',
  'paywall_offer',
  'delivered_value',
] as const;

function PromiseLineageSection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const promise = decision.decisionSupport?.promiseContinuity;
  const stageRefs = promise?.stageRefs ?? [];
  const byStage = new Map(stageRefs.map((stage) => [stage.stage, stage]));
  return (
    <Section title="Promise-to-value lifecycle" testId={CREATIVE_DECISIONS_TEST_IDS.drawerPromiseLineage}>
      {!promise || promise.status !== 'available' ? (
        <Alert severity="info">
          Immutable ad, landing, quiz/result, paywall, and delivered-value evidence is unavailable. Segmently will not manufacture message continuity from missing stages.
        </Alert>
      ) : (
        <Stack spacing={1.25}>
          <Alert severity="info">
            Diagnostic only. Promise similarity cannot raise the evidence class or trigger scale/stop.
          </Alert>
          {PROMISE_STAGE_ORDER.map((stage) => {
            const evidence = byStage.get(stage);
            return (
              <Box key={stage} sx={{ display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr)', gap: 1, alignItems: 'start' }}>
                <Typography variant="caption" color="text.secondary">{stage.replaceAll('_', ' ')}</Typography>
                <Box>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {evidence?.state === 'available' && evidence.snapshotRef
                      ? evidence.snapshotRef
                      : 'missing immutable stage reference'}
                  </Typography>
                  {evidence?.promiseKeys?.length ? (
                    <Typography variant="caption" color="text.secondary">
                      {evidence.promiseKeys.map((key) => key.replaceAll('_', ' ')).join(' · ')}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
          {promise.edgeDiagnostics?.map((edge) => (
            <Alert
              key={`${edge.from}:${edge.to}`}
              severity={edge.state === 'conflicting' ? 'warning' : edge.state === 'missing' ? 'info' : 'success'}
            >
              {`${edge.from.replaceAll('_', ' ')} → ${edge.to.replaceAll('_', ' ')}: ${edge.state}${edge.diagnosticScore === undefined ? '' : ` · ${formatPercentValue(edge.diagnosticScore)}`}`}
            </Alert>
          ))}
          {stageRefs.length === 0 && (
            <Alert severity="warning">
              Only a legacy aggregate continuity diagnostic is available; stage-level decisions remain blocked until immutable refs are present.
            </Alert>
          )}
          {promise.outcomeDiagnostic ? (
            <Box sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Post-purchase outcome diagnostic
              </Typography>
              <DefinitionGrid rows={[
                ['Authority', 'descriptive, not causal'],
                ['Cohort', promise.outcomeDiagnostic.cohortRef],
                ['Window', formatDateRange(
                  promise.outcomeDiagnostic.observationWindow.from,
                  promise.outcomeDiagnostic.observationWindow.to,
                )],
                ['Maturity', promise.outcomeDiagnostic.maturityState],
                ['Coverage', `${promise.outcomeDiagnostic.coverage.numerator.toLocaleString()} / ${promise.outcomeDiagnostic.coverage.denominator.toLocaleString()}`],
                ['Value basis', [promise.outcomeDiagnostic.valueBasis, promise.outcomeDiagnostic.currency].filter(Boolean).join(' · ')],
                ['Refund delta', formatPromiseOutcomeSignal(promise.outcomeDiagnostic.outcomeSignals.refund)],
                ['Dispute delta', formatPromiseOutcomeSignal(promise.outcomeDiagnostic.outcomeSignals.dispute)],
                ['Renewal delta', formatPromiseOutcomeSignal(promise.outcomeDiagnostic.outcomeSignals.renewal)],
                ['Complaint delta', formatPromiseOutcomeSignal(promise.outcomeDiagnostic.outcomeSignals.complaint)],
                ['Valid until', formatDateTime(promise.outcomeDiagnostic.validUntil)],
              ]} />
              <Alert severity="info" sx={{ mt: 1 }}>
                These cohort deltas help investigate an expectation gap. They cannot prove that message continuity caused refunds, disputes, cancellations, renewals, or complaints.
              </Alert>
            </Box>
          ) : (
            <Alert severity="info">
              Refund, dispute, renewal, cancellation, and complaint linkage is unavailable for this exact cohort. Missing outcomes are not treated as zero.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            {`Model ${promise.modelId ?? 'n/a'} ${promise.modelVersion ?? ''} · evaluated ${formatDateTime(promise.evaluatedAt)}`}
          </Typography>
        </Stack>
      )}
    </Section>
  );
}

function formatPromiseOutcomeSignal(
  signal: PromiseOutcomeSignalDiagnostic,
): string {
  if (!signal || typeof signal !== 'object' || !('state' in signal) || signal.state !== 'available') {
    return signal && typeof signal === 'object' && 'unavailableReasonCode' in signal
      ? String(signal.unavailableReasonCode).replaceAll('_', ' ')
      : 'unavailable';
  }
  const available = signal as { delta?: number; numerator?: number; denominator?: number };
  return `${formatPercentValue(available.delta)} · ${available.numerator?.toLocaleString() ?? 'n/a'} / ${available.denominator?.toLocaleString() ?? 'n/a'}`;
}

function ContextFact({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box sx={{ p: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

type DeploymentComparisonContext = NonNullable<
  NonNullable<CreativeDecisionProjection['artifactContext']>['deploymentContexts']
>[number];

type ComparisonStatistics = NonNullable<DeploymentComparisonContext['comparisonStatistics']>;

function ContextComparisonStatistics({
  decision,
  context,
}: {
  decision: CreativeDecisionProjection;
  context: DeploymentComparisonContext;
}): React.ReactElement {
  const statistics = resolveContextStatistics(decision, context);
  const sample = statistics
    ? `raw ${formatSampleCount(statistics.rawSampleCount)} · effective ${formatSampleCount(statistics.effectiveSampleCount)}`
    : 'unavailable';
  const shrinkage = statistics
    ? [
      statistics.shrinkageLevel?.replaceAll('_', ' '),
      statistics.shrinkageWeight !== undefined ? `weight ${formatPercent(statistics.shrinkageWeight)}` : undefined,
      statistics.poolingParentKey ? `parent ${statistics.poolingParentKey}` : undefined,
    ].filter(Boolean).join(' · ') || 'unavailable'
    : 'unavailable';
  const dispersion = statistics?.dispersionMetric
    ? [
      statistics.dispersionMetric.replaceAll('_', ' '),
      `value ${formatRoas(statistics.dispersionValue)}`,
      `median ${formatRoas(statistics.dispersionMedian)}`,
      `distance ${formatRoas(statistics.absoluteDistanceFromMedian)}`,
    ].join(' · ')
    : 'unavailable';
  return (
    <Box
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerContextStatistics(context.deploymentKey)}
      sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>Comparison statistics</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={statistics?.state === 'available' ? 'success' : statistics?.state === 'partial' ? 'warning' : 'default'}
          label={statistics?.state ?? 'unavailable'}
        />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
        <ContextFact label="Sample" value={sample} />
        <ContextFact label="Shrinkage" value={shrinkage} />
        <ContextFact label="Dispersion" value={dispersion} />
      </Box>
      {statistics?.reasonCodes.length ? (
        <Typography variant="caption" color="text.secondary">
          {statistics.reasonCodes.map((reason) => reason.replaceAll('_', ' ')).join(' · ')}
        </Typography>
      ) : null}
    </Box>
  );
}

function resolveContextStatistics(
  decision: CreativeDecisionProjection,
  context: DeploymentComparisonContext,
): ComparisonStatistics | undefined {
  if (context.comparisonStatistics) return context.comparisonStatistics;
  if (context.deploymentKey !== decision.deployment.deploymentKey) return undefined;
  const prediction = decision.decisionSupport?.predictionTrust;
  const metric = decision.artifactContext?.dispersion?.metric;
  const dispersionValue = metric === 'realized_contribution_roas'
    ? context.realizedContributionRoas
    : metric === 'projected_contribution_roas_p50'
      ? context.projectedContributionRoasP50
      : undefined;
  const hasSample = prediction?.status === 'available'
    && (prediction.rawSampleCount !== undefined || prediction.effectiveSampleCount !== undefined);
  if (!hasSample && dispersionValue === undefined) return undefined;
  return {
    state: 'partial',
    ...(prediction?.rawSampleCount !== undefined ? { rawSampleCount: prediction.rawSampleCount } : {}),
    ...(prediction?.effectiveSampleCount !== undefined ? { effectiveSampleCount: prediction.effectiveSampleCount } : {}),
    ...(prediction?.shrinkageLevel ? { shrinkageLevel: prediction.shrinkageLevel } : {}),
    ...(prediction?.shrinkageWeight !== undefined ? { shrinkageWeight: prediction.shrinkageWeight } : {}),
    ...(prediction?.poolingParentKey ? { poolingParentKey: prediction.poolingParentKey } : {}),
    ...(metric ? { dispersionMetric: metric } : {}),
    ...(dispersionValue !== undefined ? { dispersionValue } : {}),
    reasonCodes: ['context_statistics_partially_projected'],
  };
}

function formatArtifactDispersion(
  dispersion: NonNullable<CreativeDecisionProjection['artifactContext']>['dispersion'],
): string {
  if (!dispersion || dispersion.state !== 'available') return 'unavailable';
  return `${formatRoas(dispersion.minimum)}–${formatRoas(dispersion.maximum)} · range ${formatRoas(dispersion.absoluteRange)} · ${dispersion.evidenceClass ?? 'descriptive'} only`;
}

function artifactMonetizationLabel(
  context: NonNullable<CreativeDecisionProjection['monetizationContexts']>[number],
): string {
  return [
    context.purchaseMode.replaceAll('_', ' '),
    context.subscriptionEntry.replaceAll('_', ' '),
    context.priceId,
  ].filter(Boolean).join(' / ');
}

function MonetizationSection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const context = decision.monetization;
  const contexts = decision.monetizationContexts ?? [];
  const money = decision.money;
  return (
    <Section title="Monetization context" testId={CREATIVE_DECISIONS_TEST_IDS.drawerMonetization}>
      <DefinitionGrid rows={[
        ['Distinct contexts', contexts.length ? String(contexts.length) : 'n/a'],
        ['Purchase mode', context.purchaseMode.replaceAll('_', ' ')],
        ['Subscription entry', context.subscriptionEntry.replaceAll('_', ' ')],
        ['Product / price', [context.productId, context.priceId].filter(Boolean).join(' / ') || 'n/a'],
        ['Offer', context.offerId ?? 'n/a'],
        ['Billing interval', context.billingInterval ? `${context.intervalCount ?? 1} ${context.billingInterval}` : 'n/a'],
        ['Original currency', money.selectedWindowSpend?.originalCurrency ?? 'n/a'],
        ['Reporting currency', money.selectedWindowSpend?.reportingCurrency ?? 'n/a'],
      ]} />
      {contexts.length > 1 && (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Results span multiple monetization contexts; they remain separate and are not presented as one blended plan.
          </Typography>
          {contexts.map((item, index) => (
            <Box key={`${item.purchaseMode}:${item.subscriptionEntry}:${item.productId ?? ''}:${item.priceId ?? ''}:${index}`} sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="body2">
                {[
                  item.purchaseMode.replaceAll('_', ' '),
                  item.subscriptionEntry.replaceAll('_', ' '),
                  [item.productId, item.priceId].filter(Boolean).join(' / '),
                  item.offerId ? `offer ${item.offerId}` : '',
                  item.billingInterval ? `${item.intervalCount ?? 1} ${item.billingInterval}` : '',
                ].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Section>
  );
}

function DecisionTrustSection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  const support = decision.decisionSupport;
  const prediction = support?.predictionTrust;
  const statusLine = (
    label: string,
    item: { status: 'available' | 'unavailable'; reasonCodes: string[] } | undefined,
  ): [string, string] => [
    label,
    item?.status === 'available'
      ? 'available'
      : `unavailable · ${(item?.reasonCodes.length ? item.reasonCodes : ['evidence_not_available'])
        .map((reason) => reason.replaceAll('_', ' ')).join(' · ')}`,
  ];
  return (
    <Section title="Decision trust & next evidence" testId={CREATIVE_DECISIONS_TEST_IDS.drawerTrust}>
      <DefinitionGrid rows={[
        statusLine('Prediction trust', prediction),
        statusLine('Randomized / factorial interaction', support?.randomizedOrFactorialInteraction),
        statusLine('Validated treatment surrogate', support?.validatedSurrogate),
        statusLine('Incrementality calibration', support?.incrementality),
        statusLine('Guardrails', support?.guardrails),
        statusLine('Fatigue', support?.fatigue),
        statusLine('Next experiment', support?.nextExperiment),
      ]} />
      {prediction?.status === 'available' && (
        <PredictionTrustDetails prediction={prediction} />
      )}
      {support?.randomizedOrFactorialInteraction?.status === 'available' && (
        <FactorialTrustDetails factorial={support.randomizedOrFactorialInteraction} />
      )}
      {support?.guardrails?.status === 'available' && support.guardrails.blockingMetricKeys?.length ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {`Blocking consumer-harm metrics: ${support.guardrails.blockingMetricKeys
            .map((metric) => metric.replaceAll('_', ' '))
            .join(' · ')}.`}
        </Alert>
      ) : null}
      {support?.validatedSurrogate?.status === 'available' && (
        <SurrogateTrustDetails surrogate={support.validatedSurrogate} />
      )}
      {support?.incrementality?.status === 'available' && (
        <IncrementalityTrustDetails incrementality={support.incrementality} />
      )}
      {support?.fatigue?.status === 'available' && <FatigueTrustDetails fatigue={support.fatigue} />}
      {support?.fatigue?.status !== 'available' && support?.fatigue?.state === 'watch' && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {`Early-window watch: ${(support.fatigue.earlyWatchReasons ?? [])
            .map((reason) => reason.replaceAll('_', ' ')).join(' · ') || 'short-window signal'}. `}
          Confirmatory 28-day change-point evidence does not exist yet for this creative — this is a short-window signal for the refresh decision, never a verdict.
        </Alert>
      )}
      {support?.fatigue?.status !== 'available'
        && support?.fatigue?.state !== 'watch'
        && support?.fatigue?.reasonCodes.includes('fatigue_evidence_missing') && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          No qualifying fatigue window yet: the creative needs 28 consecutive complete delivery days and powered volume (~40k impressions) before the confirmatory change-point tier can speak. Powered creatives carry ~94% of account impressions.
        </Typography>
      )}
      {support?.nextExperiment?.status === 'available' && (
        <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Registered next experiment</Typography>
          <DefinitionGrid rows={[
            ['Experiment kind', support.nextExperiment.experimentKind?.replaceAll('_', ' ') ?? 'n/a'],
            ['Estimand', support.nextExperiment.estimandRef ?? 'n/a'],
            ['Primary metric', support.nextExperiment.primaryMetricRef ?? 'n/a'],
            ['MDE', formatPercentValue(support.nextExperiment.minimumDetectableEffect)],
            ['Minimum duration', support.nextExperiment.minimumDurationDays !== undefined ? `${support.nextExperiment.minimumDurationDays} days` : 'required before start'],
            ['Stopping policy', support.nextExperiment.stoppingPolicyRef ?? 'required before start'],
            ['Fixed factors', support.nextExperiment.fixedFactors?.join(' · ') || 'n/a'],
            ['Factors to vary', support.nextExperiment.factorsToVary?.join(' · ') || 'n/a'],
            ['Guardrails', support.nextExperiment.guardrailMetricRefs?.join(' · ') || 'n/a'],
          ]} />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Unavailable evidence is never inferred from provider delivery, gross pLTV, or funnel correlations.
      </Typography>
      {support?.repairActions?.length ? (
        <Stack spacing={0.5} sx={{ mt: 1.25 }}>
          {support.repairActions.map((action) => (
            <Typography key={action.actionKey} variant="body2">
              {`${action.label} · ${action.reasonCodes.map((reason) => reason.replaceAll('_', ' ')).join(' · ')}`}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
          No registered repair action is available.
        </Typography>
      )}
    </Section>
  );
}

type PredictionTrust = NonNullable<
  NonNullable<CreativeDecisionProjection['decisionSupport']>['predictionTrust']
>;

function PredictionTrustDetails({ prediction }: { prediction: PredictionTrust }): React.ReactElement {
  const model = [prediction.modelId, prediction.modelVersion].filter(Boolean).join(' · ') || 'n/a';
  const calibration = [
    prediction.calibrationState?.replaceAll('_', ' '),
    prediction.calibrationRevisionId,
  ].filter(Boolean).join(' · ') || 'n/a';
  const target = [
    prediction.targetHorizonKey,
    prediction.targetHorizonDays !== undefined ? `${prediction.targetHorizonDays} days` : undefined,
    prediction.targetBasis?.replaceAll('_', ' '),
  ].filter(Boolean).join(' · ') || 'n/a';
  const sample = `raw ${formatSampleCount(prediction.rawSampleCount)} · effective ${formatSampleCount(prediction.effectiveSampleCount)}`;
  const shrinkage = [
    prediction.shrinkageLevel?.replaceAll('_', ' '),
    prediction.shrinkageWeight !== undefined ? `weight ${formatPercent(prediction.shrinkageWeight)}` : undefined,
  ].filter(Boolean).join(' · ') || 'n/a';

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Observational prediction details
      </Typography>
      <DefinitionGrid rows={[
        ['Model', model],
        ['Model registry revision', prediction.modelRegistryRevisionId ?? 'n/a'],
        ['Model state at prediction', prediction.modelLifecycleStateAtPrediction?.replaceAll('_', ' ') ?? 'n/a'],
        ['Calibration', calibration],
        ['Target horizon', target],
        ['Cohort started', formatDateTime(prediction.cohortStartedAt)],
        ['Prediction origin', prediction.predictionOriginDay !== undefined ? `D${prediction.predictionOriginDay}` : 'n/a'],
        ['Feature cutoff', formatDateTime(prediction.predictionCutoffAt)],
        ['Prediction as of', formatDateTime(prediction.predictedAt)],
        ['Target matures', formatDateTime(prediction.targetMaturityDueAt)],
        ['Sample size', sample],
        ['Shrinkage', shrinkage],
        ['Pooling parent', prediction.poolingParentKey ?? 'not provided'],
        ['Snapshot expires', formatDateTime(prediction.expiresAt)],
        ['Decision evidence valid until', formatDateTime(prediction.validUntil)],
        ['Evaluation window', formatDateRange(prediction.evaluationWindowFrom, prediction.evaluationWindowTo)],
        ['Mature cohorts', formatSampleCount(prediction.matureCohortCount)],
        ['Bias', formatNumber(prediction.bias)],
        ['Mean absolute error', formatNumber(prediction.meanAbsoluteError)],
        ['CRPS', formatNumber(prediction.crps)],
        ['Log score', formatNumber(prediction.logScore)],
        ['P50 empirical coverage', formatPercentValue(prediction.empiricalCoverageP50)],
        ['P80 empirical coverage', formatPercentValue(prediction.empiricalCoverageP80)],
        ['P90 empirical coverage', formatPercentValue(prediction.empiricalCoverageP90)],
        ['P50 / P80 / P90 sharpness', [prediction.sharpnessP50, prediction.sharpnessP80, prediction.sharpnessP90].map(formatNumber).join(' · ')],
        ['False scale / false kill', `${formatPercentValue(prediction.falseScaleRate)} / ${formatPercentValue(prediction.falseKillRate)}`],
      ]} />
      <Alert severity="info" sx={{ mt: 1.5 }}>
        Expected cohort contribution is observational. It does not show that the creative caused lift or that spend was incremental.
      </Alert>
      <CreativeActualVsPredictedCurve curve={prediction.actualVsPredictedCurve} />
    </Box>
  );
}

type FactorialTrust = NonNullable<
  NonNullable<CreativeDecisionProjection['decisionSupport']>['randomizedOrFactorialInteraction']
>;

function FactorialTrustDetails({ factorial }: { factorial: FactorialTrust }): React.ReactElement {
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Creative × funnel factorial result</Typography>
      <DefinitionGrid rows={[
        ['Experiment', factorial.experimentId ?? 'n/a'],
        ['Estimand', factorial.estimandRef ?? 'n/a'],
        ['Interaction state', factorial.interactionState ?? 'unknown'],
        ['Creative main effect', formatEffect(factorial.creativeMainEffect)],
        ['Funnel main effect', formatEffect(factorial.funnelMainEffect)],
        ['Creative × funnel interaction', formatEffect(factorial.interactionEffect)],
      ]} />
      {factorial.cellSizes?.length ? (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {factorial.cellSizes.map((cell) => (
            <Typography key={`${cell.creativeLevel}:${cell.funnelLevel}`} variant="caption" color="text.secondary">
              {`${cell.creativeLevel} × ${cell.funnelLevel}: ${cell.assignedUnits} assigned · ${cell.exposedUnits} exposed · ${cell.outcomeUnits} outcomes`}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Alert severity="warning" sx={{ mt: 1 }}>Exact factorial cell sizes are unavailable; no interaction verdict should be promoted.</Alert>
      )}
    </Box>
  );
}

type SurrogateTrust = NonNullable<
  NonNullable<CreativeDecisionProjection['decisionSupport']>['validatedSurrogate']
>;

function SurrogateTrustDetails({ surrogate }: { surrogate: SurrogateTrust }): React.ReactElement {
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Validated treatment surrogate</Typography>
      <DefinitionGrid rows={[
        ['Metric', surrogate.metricDefinitionRef ?? 'n/a'],
        ['Validation model', surrogate.modelVersion ?? 'n/a'],
        ['Target', [surrogate.targetBasis, surrogate.targetHorizonKey, surrogate.targetHorizonDays !== undefined ? `${surrogate.targetHorizonDays} days` : undefined, surrogate.targetCurrency].filter(Boolean).join(' · ') || 'n/a'],
        ['Consumer scope', formatSurrogateConsumerScope(surrogate)],
        ['Calibration slope / intercept', `${formatNumber(surrogate.calibrationSlope)} / ${formatNumber(surrogate.calibrationIntercept)}`],
        ['R² / coverage', `${formatNumber(surrogate.rSquared)} / ${formatPercentValue(surrogate.empiricalCoverage)}`],
        ['Treatment-effect sign agreement', formatPercentValue(surrogate.treatmentEffectSignAgreement)],
        ['False scale / false kill / abstention', `${formatPercentValue(surrogate.falseScaleRate)} / ${formatPercentValue(surrogate.falseKillRate)} / ${formatPercentValue(surrogate.abstentionRate)}`],
        ['Intervention family', surrogate.interventionFamily ?? 'n/a'],
        ['Randomized unit', surrogate.randomizedUnitRef ?? 'n/a'],
        ['Valid segments', surrogate.validSegments?.join(' · ') || 'n/a'],
        ['Drift', [surrogate.driftState, surrogate.driftPolicyVersion].filter(Boolean).join(' · ') || 'unknown'],
        ['Validity', formatDateRange(surrogate.validFrom, surrogate.validUntil)],
      ]} />
    </Box>
  );
}

function formatSurrogateConsumerScope(surrogate: SurrogateTrust): string {
  const exact = (label: string, value: string | null | undefined): string | undefined => (
    value === undefined ? undefined : `${label} ${value ?? 'unsegmented'}`
  );
  return [
    exact('source', surrogate.sourceScope),
    exact('geo', surrogate.geoScope),
    exact('funnel', surrogate.funnelFamilyScope),
    exact('creative', surrogate.creativeFamilyScope),
  ].filter(Boolean).join(' · ') || 'n/a';
}

type IncrementalityTrust = NonNullable<
  NonNullable<CreativeDecisionProjection['decisionSupport']>['incrementality']
>;

function IncrementalityTrustDetails({ incrementality }: { incrementality: IncrementalityTrust }): React.ReactElement {
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Incrementality evidence scope</Typography>
      <DefinitionGrid rows={[
        ['Design', incrementality.designKind?.replaceAll('_', ' ') ?? 'n/a'],
        ['Population', incrementality.populationRef ?? 'n/a'],
        ['Effect interval', formatInterval(incrementality.effectInterval)],
        ['Interference review', incrementality.interferenceReviewState ?? 'unknown'],
        ['Spillover review', incrementality.spilloverReviewState ?? 'unknown'],
        ['Channel scope', incrementality.channelScope ?? 'n/a'],
        ['Geo scope', incrementality.geoScope ?? 'n/a'],
        ['Creative family scope', incrementality.creativeFamilyScope ?? 'n/a'],
        ['Validity', formatDateRange(incrementality.validFrom, incrementality.validUntil)],
      ]} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Incrementality is valid only for the registered evidence scope and cannot be inherited outside this exact channel, population, geo, creative-family, and validity window.
      </Typography>
    </Box>
  );
}

type FatigueTrust = NonNullable<
  NonNullable<CreativeDecisionProjection['decisionSupport']>['fatigue']
>;

function FatigueTrustDetails({ fatigue }: { fatigue: FatigueTrust }): React.ReactElement {
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Contextual fatigue evidence</Typography>
      <DefinitionGrid rows={[
        ['State', fatigue.state ?? 'unknown'],
        ['Model', fatigue.modelVersion ?? 'n/a'],
        ['Evaluated', formatDateTime(fatigue.evaluatedAt)],
        ['Valid until', formatDateTime(fatigue.validUntil)],
        ['Change point', formatDateTime(fatigue.changePointAt)],
        ['Contextual controls', fatigue.contextualControls?.join(' · ') || 'n/a'],
        ['Frequency-only signal', fatigue.frequencyOnly === undefined ? 'unknown' : fatigue.frequencyOnly ? 'yes — diagnostic only' : 'no'],
        ['Early-watch signals', fatigue.earlyWatchReasons?.length
          ? fatigue.earlyWatchReasons.map((reason) => reason.replaceAll('_', ' ')).join(' · ')
          : 'none'],
      ]} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Watch-only contextual association — this evidence informs the human refresh decision and never enters kill/scale verdicts.
      </Typography>
    </Box>
  );
}

function SignalSection({ metrics }: { metrics: CreativeStageMetric[] }): React.ReactElement {
  return (
    <Section title="Funnel signals" testId={CREATIVE_DECISIONS_TEST_IDS.drawerSignals}>
      {metrics.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No registered stage metrics for this scope.</Typography>
      ) : (
        <Stack spacing={1}>
          {metrics.map((metric) => (
            <Stack key={metric.metricKey} direction="row" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="body2">{metric.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {`${metric.evidence.role.replaceAll('_', ' ')} · ${metric.maturity}`}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                {formatStageMetric(metric)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
      <Alert severity="info" sx={{ mt: 1.5 }}>
        Diagnostic and predictive funnel signals can help prioritize investigation. They cannot independently create a scale or stop verdict.
      </Alert>
    </Section>
  );
}

function EvidenceCopilotSection({
  decision,
  onAskEvidence,
}: {
  decision: CreativeDecisionProjection;
  onAskEvidence?: (request: CreativeDecisionCopilotAnswerRequest) => Promise<CreativeDecisionCopilotAnswer>;
}): React.ReactElement {
  const [question, setQuestion] = React.useState('Why is this creative performing this way?');
  const [result, setResult] = React.useState<CreativeDecisionCopilotAnswer | null>(null);
  const [refusal, setRefusal] = React.useState<{ code: string; message: string } | null>(null);
  const [loading, setLoading] = React.useState(false);
  const controllerRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    controllerRef.current?.abort();
    setQuestion('Why is this creative performing this way?');
    setResult(null);
    setRefusal(null);
    setLoading(false);
    return () => controllerRef.current?.abort();
  }, [decision.projectionId]);

  const ask = React.useCallback(async () => {
    const normalizedQuestion = question.trim();
    if (!normalizedQuestion) {
      setResult(null);
      setRefusal({ code: 'question_required', message: 'Enter a question about this decision.' });
      return;
    }
    const evidenceRef = decision.copilotEvidenceRef;
    if (!evidenceRef) {
      setResult(null);
      setRefusal({
        code: 'evidence_snapshot_reference_missing',
        message: 'I cannot conclude because this projection has no server-owned immutable snapshot reference.',
      });
      return;
    }
    if (!onAskEvidence) {
      setResult(null);
      setRefusal({
        code: 'copilot_service_unavailable',
        message: 'I cannot conclude because the server evidence service is unavailable.',
      });
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setResult(null);
    setRefusal(null);
    try {
      const answer = await onAskEvidence({
        snapshotId: evidenceRef.snapshotId,
        projectionHash: evidenceRef.projectionHash,
        question: normalizedQuestion,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) setResult(answer);
    } catch (error) {
      if (!controller.signal.aborted) setRefusal(copilotRequestRefusal(error));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [decision.copilotEvidenceRef, onAskEvidence, question]);

  return (
    <Section title="Ask the evidence" testId={CREATIVE_DECISIONS_TEST_IDS.drawerCopilot}>
      <Stack spacing={1.25}>
        <TextField
          size="small"
          fullWidth
          label="Question about this decision"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void ask();
          }}
          inputProps={{ 'data-testid': CREATIVE_DECISIONS_TEST_IDS.copilotQuestion }}
        />
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            onClick={() => void ask()}
            disabled={loading}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.copilotAsk}
          >
            {loading ? 'Resolving evidence…' : 'Ask evidence'}
          </Button>
          <Button size="small" onClick={() => setQuestion('What should we test next?')}>
            What should we test next?
          </Button>
        </Stack>
        {result && (
          <Alert
            severity={result.conclusionAvailable ? 'info' : 'warning'}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.copilotAnswer}
          >
            <Typography variant="body2">{result.answer}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              {result.snapshotVerified
                ? `Server-verified snapshot ${result.snapshotId} · hash ${shortHash(result.projectionHash)} · ${result.freshness} · answered ${formatDateTime(result.answeredAt)}`
                : `Snapshot request ${result.snapshotId} · hash ${shortHash(result.projectionHash)} · refused: ${result.refusalCode ?? 'evidence_unavailable'}`}
            </Typography>
            {result.metricEvidence.map((binding) => (
              <Typography key={binding.metricKey} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                {`${binding.metricKey.replaceAll('_', ' ')} · ${binding.evidenceClass} · ${binding.freshnessState} · sources ${binding.sourceRefs.join(', ')}`}
              </Typography>
            ))}
          </Alert>
        )}
        {refusal && (
          <Alert severity="warning" data-testid={CREATIVE_DECISIONS_TEST_IDS.copilotAnswer}>
            <Typography variant="body2">{refusal.message}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{`Refusal: ${refusal.code}`}</Typography>
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          The server must resolve the exact immutable snapshot ID and content hash before answering. If it cannot, you will see a typed refusal. This surface cannot change ads, budgets, experiments, or creative assets.
        </Typography>
      </Stack>
    </Section>
  );
}

function copilotRequestRefusal(error: unknown): { code: string; message: string } {
  const code = typeof error === 'object' && error && 'code' in error && typeof error.code === 'string'
    ? error.code
    : 'copilot_request_failed';
  if (code === 'capability_not_activated' || code === 'write_not_enabled') {
    return { code, message: 'I cannot conclude because the server evidence copilot is disabled.' };
  }
  if (code === 'source_unavailable_for_scope') {
    return { code, message: 'I cannot conclude because the exact server evidence is unavailable for this scope.' };
  }
  return { code, message: 'I cannot conclude because the server could not verify the immutable evidence snapshot.' };
}

function shortHash(value: string): string {
  return `${value.slice(0, 12)}…`;
}

function EvidenceSection({ decision }: { decision: CreativeDecisionProjection }): React.ReactElement {
  return (
    <Section title="Evidence & sources" testId={CREATIVE_DECISIONS_TEST_IDS.drawerEvidence}>
      <Stack spacing={1}>
        {decision.evidence.map((binding) => (
          <Box key={binding.metricKey} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>{binding.metricKey.replaceAll('_', ' ')}</Typography>
              <Chip size="small" variant="outlined" label={binding.evidenceClass} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {`${binding.measurementBasis.replaceAll('_', ' ')} · ${binding.freshnessState} · ${formatCoverage(binding.coverage)}`}
            </Typography>
          </Box>
        ))}
      </Stack>
      {decision.sourceLinks?.length ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          {decision.sourceLinks.map((source) => (
            <Link
              key={`${source.kind}:${source.href}`}
              href={source.href}
              target={source.external ? '_blank' : undefined}
              rel={source.external ? 'noreferrer' : undefined}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.sourceLink(source.kind)}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {source.label}
              {source.external && <OpenInNewIcon sx={{ fontSize: 14 }} />}
            </Link>
          ))}
        </Stack>
      ) : null}
    </Section>
  );
}

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Box component="section" data-testid={testId}>
      <Typography variant="subtitle2" sx={{ fontWeight: 750, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function DefinitionGrid({ rows }: { rows: Array<[string, string]> }): React.ReactElement {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 0.8fr) minmax(0, 1.4fr)', gap: '6px 16px' }}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

function MoneyTile({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 750 }}>{value}</Typography>
    </Box>
  );
}

export function DecisionChip({ decision }: { decision: CreativeDecisionRecommendation['decision'] }): React.ReactElement {
  const color = decision === 'controlled_scale'
    ? 'success'
    : decision === 'stop_candidate'
      ? 'error'
      : decision === 'investigate' || decision === 'refresh_due'
        ? 'warning'
        : 'default';
  return <Chip size="small" color={color} label={decision.replaceAll('_', ' ')} />;
}

function primaryActionLabel(recommendation: CreativeDecisionRecommendation): string {
  switch (recommendation.primaryAction) {
    case 'review_scale_probe': return 'Review bounded scale probe';
    case 'create_refresh_experiment': return 'Create refresh experiment draft';
    case 'open_measurement_repair': return 'Open measurement repair';
    case 'review_next_read': return 'Review next-read plan';
    case 'review_stop_evidence': return 'Review stop evidence';
    case 'open_evidence': return 'Open source evidence';
    case 'none': return 'No action available';
  }
}

function providerLabel(provider: CreativeDecisionProjection['deployment']['provider']): string {
  if (provider === 'google_ads') return 'Google Ads';
  if (provider === 'tiktok_ads') return 'TikTok Ads';
  return 'Meta Ads';
}

function formatDateTime(value: string | undefined): string {
  if (!value) return 'n/a';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatDateRange(from: string | undefined, to: string | undefined): string {
  if (!from && !to) return 'n/a';
  return `${formatDateTime(from)} → ${formatDateTime(to)}`;
}

function formatNumber(value: number | undefined): string {
  return value === undefined || !Number.isFinite(value)
    ? 'n/a'
    : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatPercentValue(value: number | undefined): string {
  return value === undefined || !Number.isFinite(value) ? 'n/a' : formatPercent(value);
}

function formatEffect(
  effect: NonNullable<
    NonNullable<
      NonNullable<CreativeDecisionProjection['decisionSupport']>['randomizedOrFactorialInteraction']
    >['interactionEffect']
  > | undefined,
): string {
  if (!effect) return 'n/a';
  return `${formatNumber(effect.estimate)} [${formatNumber(effect.lower)}, ${formatNumber(effect.upper)}] ${effect.unit.replaceAll('_', ' ')}`;
}

function formatInterval(
  interval: NonNullable<
    NonNullable<
      NonNullable<CreativeDecisionProjection['decisionSupport']>['incrementality']
    >['effectInterval']
  > | undefined,
): string {
  if (!interval) return 'n/a';
  return `P10 ${formatNumber(interval.p10)} · P50 ${formatNumber(interval.p50)} · P90 ${formatNumber(interval.p90)} ${interval.unit.replaceAll('_', ' ')}`;
}

function formatStageMetric(metric: CreativeStageMetric): string {
  if (metric.value === undefined) return 'n/a';
  if (metric.unit === 'ratio') return formatPercent(metric.value);
  if (metric.unit === 'duration_ms') return `${Math.round(metric.value)} ms`;
  return metric.value.toLocaleString();
}

function formatCoverage(coverage: CreativeDecisionProjection['evidence'][number]['coverage']): string {
  if (!coverage) return 'coverage unavailable';
  return `${coverage.numerator.toLocaleString()} / ${coverage.denominator.toLocaleString()} ${coverage.unit}`;
}
